import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "components/translations/useTranslation";
import Comment from "components/input/Comment.component";
import OKCancelButtons from "components/modals/OKCancelButtons";
import { MODAL_CONTENT_TAB_INDEX } from "utils/constants";
import { ReviewTaskActionData, ReviewTaskProps } from "types/modals";
import { getTaskUserDescription } from "utils/valueFormatter.function";
import { reviewTask, fetchDraftComment } from "components/modals/modal.actions";
import { useDispatch } from "react-redux";
import { Button, ButtonType } from "components/common/Button.component";
import { Notification, NotificationType } from "components/common/Notification.component";
import { toDisplayError } from "utils/errorHandle.function";

const ReviewTask = ({ more, closeModal, loopFocus, errorCallback, initialFocusRef }: ReviewTaskProps): React.ReactElement => {
    const { translate } = useTranslation();
    const dispatch = useDispatch();
    const [comment, setComment] = useState("");
    const [actionRunning, setActionRunning] = useState(false);
    const [displayError, setDisplayError] = useState<Record<string, any> | null>(null);

    useEffect(() => {
        dispatch(fetchDraftComment(true, more.tasks, setComment));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally run only on mount

    const handleSuccess = useCallback(() => {
        more.handledTaskCallback(more?.tasks[0]?.key, more.navigate);
        closeModal();
    }, [more, closeModal]);

    const handleError = useCallback(
        (error: Record<string, any>) => {
            setDisplayError(toDisplayError(error));
            setActionRunning(false);
        },
        [errorCallback],
    );

    const handleReview = useCallback(() => {
        if (!comment) return;

        const currentTask = more?.tasks[0];
        if (!currentTask) return;

        setActionRunning(true);
        const data: ReviewTaskActionData = {
            key: currentTask.key,
            uid: currentTask.uid,
            comment,
            taskDescription: getTaskUserDescription(currentTask),
        };

        dispatch(reviewTask(data, handleSuccess, handleError));
    }, [comment, more, dispatch, handleSuccess, handleError]);

    const handleCommentChange = useCallback((value: string) => {
        setComment(value);
        setDisplayError(null);
    }, []);

    const okEnabled = !!(comment && !actionRunning);

    return (
        <>
            <div className="ga-modal__content">
                {displayError && (
                    <div className="my-16">
                        <Notification
                            type={NotificationType.ERROR}
                            title={displayError.errorTitle}
                            description={displayError.errorMessages}
                        />
                    </div>
                )}
                <Comment
                    ref={initialFocusRef}
                    inputHint={translate("popUp.review.commentHint")}
                    translate={translate}
                    tabIndex={MODAL_CONTENT_TAB_INDEX}
                    id="comment"
                    propagateValue={handleCommentChange}
                    value={comment}
                    label={translate("popUp.review.commentLabel")}
                    labelState={`(${translate("popUp.required")})`}
                />
            </div>
            {more?.isCurrentTaskHandled ? (
                <div className="ga-modal__actions">
                    <Button
                        type={ButtonType.PRIMARY}
                        text={translate("popUp.reload")}
                        onClick={closeModal}
                        tabIndex={MODAL_CONTENT_TAB_INDEX + 6}
                        onBlur={loopFocus}
                    />
                </div>
            ) : (
                <OKCancelButtons
                    tabIndex={MODAL_CONTENT_TAB_INDEX + 1}
                    okEnabled={okEnabled}
                    okCallback={handleReview}
                    okText={translate("popUp.ok")}
                    cancelEnabled={true}
                    cancelCallback={closeModal}
                    cancelText={translate("popUp.cancel")}
                    loopFocus={loopFocus}
                />
            )}
        </>
    );
};

export default ReviewTask;
